import React, { lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { FeatureCard } from '../components/animated/FeatureCard';
import { MagneticButton } from '../components/animated/MagneticButton';
import { LogoSlider } from '../components/animated/LogoSlider';
import { ChatBlurb } from '../components/animated/ChatBlurb';
import { useScrollReveal } from '../hooks/useGSAP';
import { Users, Target, TrendingUp, Lightbulb, Award, BookOpen, Brain, Sparkles, Quote, Star, Zap, Linkedin } from 'lucide-react';
export function AboutPage() {
  const heroRef = useScrollReveal();
  const navigate = useNavigate();
  const trustedLogos = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/q33zkxUoeevpbURgJWNH4Z/Edo-State-2048x2048.png',
    alt: 'Edo State Government'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3c1y2EtqxE3cZ22j3LfvAt/Firstbank-scaled.png',
    alt: 'First Bank'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pGmJB3M7dmG185ouLH3tdL/Guinness-scaled.png',
    alt: 'Guinness'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vEe7mEmaq8bYaMkACwoyZ2/Patiti-scaled.png',
    alt: 'Patiti'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nit6GmCfjTk1NhaNrGV48S/Unilever-1-scaled.png',
    alt: 'Unilever'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6726Wr7TFseuzgbfXWVCuc/etinpower_logo.webp',
    alt: 'Etinpower'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gkxuUBnqjFnuHSrzppQc3m/Igbinedion_education_logo.jpg',
    alt: 'Igbinedion Education Centre'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fdjcuHfL5jcJervWyfDggM/Seplat_Logo_2_%281%29.jpg',
    alt: 'Seplat Energy'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nmSr7rTJkqWzi93Moxk1To/TAFTA-LOGO-1.jpg',
    alt: 'TAFTA'
  }];
  const teamMembers = [{
    name: 'Precious Imuwahen Ogbomo',
    title: 'Founder, Strategic Lead',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fgM76ZqeykjR9P5o8eiPfX/IMG_0273.jpg',
    linkedIn: 'https://www.linkedin.com/in/preciousajoonu/',
    bio: `Precious is a distinguished Learning and Development expert with over 15 years of experience driving strategy execution, organizational development, and large-scale skills transformation across Africa. She led the Jobberman Soft Skills Training Programme, upskilling over 2 million young people across the continent.

She served as Pioneer Director General of the John Odigie-Oyegun Public Service Academy and holds an MSc in Management Innovation & Change (with Distinction) from the University of Aberdeen. Precious is a certified World Bank Development Knowledge Facilitator and member of CIPM.`
  }, {
    name: 'Osaze Ighoretin Ogbomo',
    title: 'Chief Operating Officer (COO) and Legal Adviser',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8Txkd1YDMxJFMykKsH1KR4/Osaze.jpg',
    linkedIn: 'https://www.linkedin.com/in/osaze-ogbomo-ll-b-b-l-llm-82b48945/',
    bio: `Osaze Ogbomo is a seasoned professional with over 11 years of experience in law, governance, and business reforms. He specializes in legal advisory, policy management, procurement, monitoring and evaluation frameworks, and MSME empowerment.

He holds an LLM in International Commercial Law from Brunel University, London, an LLB from KNUST, Ghana, and a Barrister-at-Law (BL) qualification from the Nigerian Law School. He is certified in Business Writing, CCNA, and Project Management (PMP).`
  }, {
    name: 'Paul Omugbe',
    title: 'Chief Financial Officer',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1x9yFWHxtEAPobV3pvsTLH/Paul.jpg',
    linkedIn: 'https://www.linkedin.com/in/detroyx/',
    bio: `Paul brings over 12 years of expertise in strategy, financial management, project management, and business administration. Notable achievements include managing projects for PPPRA, conducting a forensic review for NNPC, and training over 3,000 Edo State civil servants in project management.

Paul is a Fellow of the Institute of Chartered Accountants of Nigeria (ICAN) and a Chartered Professional Accountant (CPA Ontario). He holds a BSc in Applied Accounting from Oxford Brookes University and is a Certified Project Management Professional (PMP).`
  }, {
    name: 'Adeola Andrea Peregrino',
    title: 'Creative Director',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/q3S49Wz9DXAgMp7a2rCgLQ/Adeola.jpg',
    linkedIn: 'https://www.linkedin.com/in/adeola-andrea-peregrino-36b13b112/',
    bio: `Adeola Peregrino is a visionary leader in media and creative consultancy, specializing in frameworks that drive job creation and sectoral growth. She spearheaded Jobberman's Digital Skills course, reaching 1,500 learners with measurable employment outcomes.

A seasoned creative producer, Adeola has led government-commissioned documentaries including The Edo Story for Edo State Government and Leave No One Behind for Lagos State Government. She holds a certification in storytelling from the New York Film Academy.`
  }, {
    name: 'Adesuwa Elizabeth',
    title: 'Chief Technology Officer (CTO)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e6LzL4BxudY5v6GQ4RKeKG/Adesuwa.jpg',
    linkedIn: 'https://www.linkedin.com/in/adesuwa-ojo-096173140/',
    bio: `Adesuwa is a seasoned IT professional with over a decade of experience in data engineering, project management, and business operations. She specializes in data migration, system integration, Agile project management, and business process optimization.

Adesuwa holds an MBA from the University of Aberdeen and an MSc in Management Information Systems from the University of Houston-Clear Lake. She is a certified Project Management Professional (PMP), Scrum Master, and holds a Microsoft Azure Data Fundamentals certification.`
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-464">
      <main className="flex-grow pt-20" data-id="element-465">
        {/* Hero Section with Gradient Background and Chat Blurbs */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-id="element-466">
          {/* Background Elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-467" />
          <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-468" />
          <div className="absolute bottom-20 left-10 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-469" />

          {/* Chat Blurbs - Updated with real student names, no roles */}
          <ChatBlurb name="Abdullahi Ismaila Osianor" message="SkillzHQ transformed how I think, work, and communicate professionally! 🚀" avatarUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/ugttQfWmUv3yN5nuNY9y5K/IMG_7014.png" position="left" data-id="element-470" />
          <ChatBlurb name="Eunice Olabode" message="This course was an eye-opener. Best training investment! 💯" avatarUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/9FZKDGZQqf4JiekCQp6n7L/IMG_6037.jpg" position="right" data-id="element-471" />
          <ChatBlurb name="Ayobami Phebian Mehunro" message="Nothing short of exceptional. Culture change that works! ✨" avatarUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/ugttQfWmUv3yN5nuNY9y5K/IMG_7014.png" position="center" data-id="element-472" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-473">
            <div className="max-w-5xl mx-auto text-center" ref={heroRef as React.RefObject<HTMLDivElement>} data-id="element-474">
              <div className="inline-block mb-6" data-id="element-475">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100" data-id="element-476">
                  <Sparkles className="w-4 h-4 mr-2" data-id="element-477" />
                  Transforming Organizations Across Africa
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-gray-900 mb-8 leading-tight" data-id="element-478">
                Your Business Growth
                <br data-id="element-479" />
                <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-green-600 bg-clip-text text-transparent" data-id="element-480">
                  and Learning Partner
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-481">
                SkillzHQ is an innovative management consulting and skills
                development firm focused on helping organizations align culture
                transformation with business growth.
              </p>
            </div>
          </div>
        </section>

        {/* Trusted By Logo Slider */}
        <LogoSlider logos={trustedLogos} data-id="element-482" />

        {/* Mission Statement */}
        <AnimatedSection className="py-24 bg-white" data-id="element-483">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-484">
            <div className="max-w-4xl mx-auto space-y-8 text-lg text-gray-700 leading-relaxed" data-id="element-485">
              <p data-id="element-486">
                At SkillzHQ, we empower African businesses to achieve
                sustainable, long-term success by driving leadership development
                and strategy execution while aligning culture transformation
                with financial, customer, and stakeholder metrics.
              </p>
              <p data-id="element-487">
                We are also, fundamentally a skills development company,
                partnering with organizations to empower young Africans through
                our upskilling initiatives, thereby equipping them for
                entrepreneurship or securing dignified employment opportunities.
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* Four Focus Areas - Replaced Pillars with Box Style */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-488">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-489">
            <div className="max-w-6xl mx-auto" data-id="element-490">
              <div className="text-center mb-16" data-id="element-491">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-492">
                  Our Focus Areas
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-493">
                  Comprehensive solutions for organizational excellence
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-494">
                <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100" data-id="element-495">
                  <div className="flex items-center mb-4" data-id="element-496">
                    <Users className="h-8 w-8 text-blue-600 mr-3" data-id="element-497" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-498">
                      Leadership Development
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-499">
                    Empowering leaders at all levels with practical tools and
                    strategies to foster innovation, drive growth, and build
                    resilient, high-performing teams.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100" data-id="element-500">
                  <div className="flex items-center mb-4" data-id="element-501">
                    <Target className="h-8 w-8 text-green-600 mr-3" data-id="element-502" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-503">
                      Culture Transformation
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-504">
                    Creating high-performance cultures that align values,
                    behaviors, and goals while encouraging collaboration,
                    accountability, and inspiring excellence.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100" data-id="element-505">
                  <div className="flex items-center mb-4" data-id="element-506">
                    <TrendingUp className="h-8 w-8 text-blue-600 mr-3" data-id="element-507" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-508">
                      Strategy Execution
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-509">
                    Translating visions into actionable plans, aligning teams
                    with strategic goals, optimizing resources, and navigating
                    complex transformations for measurable results.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100" data-id="element-510">
                  <div className="flex items-center mb-4" data-id="element-511">
                    <Lightbulb className="h-8 w-8 text-green-600 mr-3" data-id="element-512" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-513">
                      Youth Engagement
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-514">
                    Equipping young Africans with digital skills, tech
                    capabilities, work-integrated learning programs, and
                    entrepreneurship pathways for economic opportunity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Our Approach Section */}
        <AnimatedSection className="py-24 bg-white" data-id="element-515">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-516">
            <div className="max-w-6xl mx-auto" data-id="element-517">
              <div className="text-center mb-16" data-id="element-518">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-519">
                  Our Approach
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-520">
                  At SkillzHQ, we are committed to supporting African businesses
                  by integrating culture transformation with strategic
                  execution. Our approach is designed to drive sustainable
                  success, empower individuals, and foster innovation.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-521">
                <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100" data-id="element-522">
                  <div className="flex items-center mb-4" data-id="element-523">
                    <Target className="h-8 w-8 text-blue-600 mr-3" data-id="element-524" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-525">
                      Needs Based Approach
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-526">
                    We offer bespoke services to high growth organisations,
                    supporting their long-term visions.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100" data-id="element-527">
                  <div className="flex items-center mb-4" data-id="element-528">
                    <Users className="h-8 w-8 text-green-600 mr-3" data-id="element-529" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-530">
                      Tailored Programs
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-531">
                    We understand that every organization is unique. Our
                    programs are customized to fit the specific needs of your
                    business.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100" data-id="element-532">
                  <div className="flex items-center mb-4" data-id="element-533">
                    <TrendingUp className="h-8 w-8 text-blue-600 mr-3" data-id="element-534" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-535">
                      Expert-Driven Approach
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-536">
                    Our team consists of seasoned professionals with deep
                    expertise in leadership, culture transformation, and
                    organizational strategy.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100" data-id="element-537">
                  <div className="flex items-center mb-4" data-id="element-538">
                    <Zap className="h-8 w-8 text-green-600 mr-3" data-id="element-539" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-540">
                      Sustainable Growth Focus
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-541">
                    We ensure that everything we do supports long-term success,
                    building strong foundations for the future.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Meet the Team Section */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-542">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-543">
            <div className="text-center mb-16" data-id="element-544">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-545">
                Meet the Team
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-546">
                Led by industry experts with proven track records in leadership,
                culture transformation, and organizational strategy.
              </p>
            </div>

            <div className="max-w-6xl mx-auto space-y-24" data-id="element-547">
              {teamMembers.map((member, index) => <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 items-start`} data-id="element-548">
                  <div className="md:w-1/3" data-id="element-549">
                    <div className="aspect-square rounded-3xl overflow-hidden shadow-xl border-4 border-white ring-1 ring-gray-100 hover:shadow-2xl transition-shadow duration-300" data-id="element-550">
                      <img src={member.image} alt={member.name} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-551" />
                    </div>
                  </div>
                  <div className="md:w-2/3" data-id="element-552">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-553">
                      {member.name}
                    </h3>
                    <p className="text-blue-600 font-medium mb-4 text-lg" data-id="element-554">
                      {member.title}
                    </p>
                    <a href={member.linkedIn} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 mb-6" data-id="element-555">
                      <Linkedin className="w-4 h-4" data-id="element-556" />
                      LinkedIn
                    </a>
                    <div className="text-gray-700 space-y-4 whitespace-pre-line leading-relaxed" data-id="element-557">
                      {member.bio}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </AnimatedSection>

        {/* Vision & Mission */}
        <AnimatedSection className="py-24 bg-white" data-id="element-558">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-559">
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-560">
              <div className="relative p-12 rounded-3xl bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden group hover:shadow-2xl transition-shadow duration-300" data-id="element-561">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full filter blur-3xl opacity-30 group-hover:opacity-50 transition-opacity" data-id="element-562" />
                <div className="relative" data-id="element-563">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-564">
                    Our Vision
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-565">
                    To empower people, who transform organisations
                  </p>
                </div>
              </div>

              <div className="relative p-12 rounded-3xl bg-gradient-to-br from-green-50 to-green-100 overflow-hidden group hover:shadow-2xl transition-shadow duration-300" data-id="element-566">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full filter blur-3xl opacity-30 group-hover:opacity-50 transition-opacity" data-id="element-567" />
                <div className="relative" data-id="element-568">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-569">
                    Our Mission
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-570">
                    To cultivate a culture of continuous learning and equip
                    African businesses with a highly skilled workforce
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Soft Skills University */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-571">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-572">
            <div className="max-w-6xl mx-auto" data-id="element-573">
              <div className="text-center mb-16" data-id="element-574">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-575">
                  SkillzHQ Soft Skills University
                </h2>
                <p className="text-xl text-gray-600" data-id="element-576">
                  Affordable, accessible education for the next generation
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-577">
                <FeatureCard icon={<BookOpen className="w-7 h-7 text-blue-600" data-id="element-579" />} title="Online Learning Platform" description="Courses in technology, business, design, communication, and personal development—crafted by industry experts." data-id="element-578" />
                <FeatureCard icon={<Award className="w-7 h-7 text-green-600" data-id="element-581" />} title="Skill Assessments" description="Track progress with milestone-based goals, keeping learners motivated along the way." data-id="element-580" />
                <FeatureCard icon={<Brain className="w-7 h-7 text-blue-600" data-id="element-583" />} title="AI-Powered Learning" description="Personalized learning journeys based on each learner's goals, skill level, and preferences." data-id="element-582" />
                <FeatureCard icon={<Sparkles className="w-7 h-7 text-green-600" data-id="element-585" />} title="Micro-Credentials" description="Industry-recognized certifications that showcase expertise and give learners a competitive edge." data-id="element-584" />
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* What Our Students Say - Updated with full names */}
        <AnimatedSection className="py-24 bg-white" data-id="element-586">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-587">
            <div className="text-center mb-16" data-id="element-588">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-589">
                What Our Learners Say
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-590">
                Real stories from EA Course graduates
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-591">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100 shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-592">
                <div className="flex gap-1 mb-4" data-id="element-593">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-594" />)}
                </div>
                <Quote className="w-8 h-8 text-blue-600 mb-4" data-id="element-595" />
                <p className="text-gray-700 mb-6 leading-relaxed italic" data-id="element-596">
                  "The Executive Assistant Mastery Course transformed how I
                  think, work, and communicate professionally. It exceeded every
                  expectation."
                </p>
                <div data-id="element-597">
                  <p className="font-bold text-gray-900" data-id="element-598">
                    Abdullahi Ismaila Osianor
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-599">
                    Aspiring Executive Assistant
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-100 shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-600">
                <div className="flex gap-1 mb-4" data-id="element-601">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-602" />)}
                </div>
                <Quote className="w-8 h-8 text-green-600 mb-4" data-id="element-603" />
                <p className="text-gray-700 mb-6 leading-relaxed italic" data-id="element-604">
                  "This course was an eye-opener. The modules were practical,
                  detailed, and relevant. I now feel more confident in my EA
                  journey."
                </p>
                <div data-id="element-605">
                  <p className="font-bold text-gray-900" data-id="element-606">Eunice Olabode</p>
                  <p className="text-sm text-gray-600" data-id="element-607">
                    Practicing Executive Assistant
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100 shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-608">
                <div className="flex gap-1 mb-4" data-id="element-609">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-610" />)}
                </div>
                <Quote className="w-8 h-8 text-blue-600 mb-4" data-id="element-611" />
                <p className="text-gray-700 mb-6 leading-relaxed italic" data-id="element-612">
                  "Nothing short of exceptional. The course broadened my
                  understanding and gave me clarity on how to excel as a
                  world-class EA."
                </p>
                <div data-id="element-613">
                  <p className="font-bold text-gray-900" data-id="element-614">
                    Ayobami Phebian Mehunro
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-615">
                    Aspiring Executive Assistant
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* CTA */}
        <AnimatedSection className="py-32 bg-gradient-to-br from-blue-600 to-green-600 relative overflow-hidden" data-id="element-616">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-617" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-618">
            <div className="max-w-4xl mx-auto text-center" data-id="element-619">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-620">
                Join Our Learning Community
              </h2>
              <p className="text-xl text-blue-100 mb-12" data-id="element-621">
                Connect, collaborate, and fast-track your development
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-622">
                <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/courses')} data-id="element-623">
                  Get Started
                </MagneticButton>
                <MagneticButton variant="outline" size="lg" onClick={() => navigate('/contact')} data-id="element-624">
                  Contact Us
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}